/**
 *
 * @param input Should be a Date or String, String should be in Iso format YYYY-MM-DDTHH:mm:ss.sssZ. or in dd-MM-YYYY
 */
export interface Dates {
  year: number;
  month: number;
  day: number;
  dateString: number;
}

export const isInvalidDate = (date: Date) => Number.isNaN(date.getTime());

/**
 * Parse a DateOnly string (i.e. "2024-06-05") as local time, such that the resulting Date object is midnight on the given date in the local timezone.
 * @param input String to parse. If passing in undefined or null, undefined will be returned.
 * @return The parsed date object, or undefined if the date could not be parsed.
 */
export function parseDateOnlyAsLocalTime(input: string | undefined | null): Date | undefined {
  if (!input) return undefined;

  const timeIndex = input.indexOf('T');
  const dateToParse = `${timeIndex >= 0 ? input.substring(0, timeIndex) : input}T00:00`;
  const date = new Date(dateToParse);

  if (Number.isNaN(date.getTime())) return undefined;
  return date;
}

export function parseDateWithTimeZone(input: string | Date): Date {
  if (typeof input !== 'string') {
    return input;
  }

  if (isIsoDate(input)) {
    const tzoffset = new Date().getTimezoneOffset() * 60000;
    input = new Date(new Date(input).getTime() - tzoffset).toLocaleDateString('nl');
  }
  return parseDate(input);
}

export function isIsoDate(str: string) {
  const isoDateRegExp =
    /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;

  return isoDateRegExp.test(str);
}

export function parseDateToIsoString(date: string) {
  if (!date) return '';
  return parseDate(date).toISOString();
}

export function parseDate(date: string) {
  const [day, month, year] = date.split('-');
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
}

export function makeDate(d: Date): Dates {
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    dateString: d.setHours(0, 0, 0, 0),
  };
}

export function formatDate(value: Dates): string {
  const { year, month, day } = value;
  const corrYear = year;
  const corrMonth = month && month < 10 ? `0${month}` : month;
  const corrDay = day && day < 10 ? `0${day}` : day;
  return `${corrDay}-${corrMonth}-${corrYear}`;
}

export function tryFormatDate(dateStringOrDate: string | Date): string {
  const date = dateStringOrDate instanceof Date ? dateStringOrDate : Date.parse(dateStringOrDate);
  if (Number.isNaN(date)) {
    return dateStringOrDate as string;
  }
  return new Date(date).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function tryFormatDateTime(dateString: string, includeSeconds = false): string {
  const options = includeSeconds
    ? undefined
    : {
        dateStyle: 'short',
        timeStyle: 'short',
      };
  return tryFormatDateToLocal(dateString, options);
}

export function tryFormatDateTimeWithMonthName(dateString: string, withTime = true): string {
  const timestamp = Date.parse(dateString);
  if (Number.isNaN(timestamp)) {
    return dateString;
  }

  const locales = 'nl-NL';
  const date = new Date(timestamp);
  const dateResult = date.toLocaleDateString(locales, {
    dateStyle: 'long',
  });

  if (!withTime) {
    return dateResult;
  }

  const timeResult = date.toLocaleTimeString(locales, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return [dateResult, timeResult].join(' ');
}

function tryFormatDateToLocal(
  dateString: string,
  options?: Intl.DateTimeFormatOptions | any,
): string {
  const date = Date.parse(dateString);
  if (Number.isNaN(date)) {
    return dateString;
  }
  return new Date(date).toLocaleString('nl-NL', options);
}

export function toDateonly(date: Date): string {
  // nl-Nl gebruiken voor de juiste timezone daarna omzetten naar YYYY-MM-dd formaat.
  const dateParts = date
    .toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .slice(0, 10)
    .split('-');

  return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
}

export function currentFormattedDate() {
  return new Date().toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function isValidValue(value: string | undefined) {
  const regex =
    /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/i;
  return (value && regex.test(value)) as boolean;
}

export function convertTimeToFormat(dateObj: Date) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export function toDate(dateString: string): undefined | Date {
  const date = Date.parse(dateString);
  if (Number.isNaN(date)) {
    return undefined;
  }
  return new Date(date);
}

export function toUtcDate(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day));
}

export function getNumberOfDaysInPast(date: Date) {
  return Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
}

export function addDays(date: Date, daysToAdd: number) {
  return new Date(date.getTime() + 1000 * 60 * 60 * 24 * daysToAdd);
}

export function formatDateToDDMMYYYY(dateStringOrDate: string | Date): string {
  const date = dateStringOrDate instanceof Date ? dateStringOrDate : new Date(dateStringOrDate);
  if (Number.isNaN(date)) {
    return dateStringOrDate as string;
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // maand is 0-based
  const yyyy = date.getFullYear();
  return `${dd}${mm}${yyyy}`;
}
